import { useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { showToast } from '@/contexts/ToastContext';
import { hasStringValue } from '@/utils/validation';
import { convertInputDateToServerTime, convertInputDateToEndOfDayServerTime } from '@/utils/date-time';
import { openPage } from '@/utils/dom';
import { displayNocValuesForGridTemplate } from '@/utils/formatting';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Dropdownlist } from '@/components/Dropdownlist';
import { KstDataGrid } from '@/components/KstDataGrid';

export function CaseReportsScreen() {
  const { caseReportNpsdClientList } = useAuth();

  const [caseId, setCaseId] = useState('');
  const [location, setLocation] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(caseReportNpsdClientList.length > 0 ? caseReportNpsdClientList[0].id : '');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [gridEntries, setGridEntries] = useState<any[]>([]);
  const [gridPager, setGridPager] = useState({ curPage: 1, numPages: 1, records: 0, isVisible: false, pageSize: 20 });
  const [gridLoading, setGridLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState<Record<number, boolean>>({});
  const [activePickerKey, setActivePickerKey] = useState<string | null>(null);
  const currentPageRef = useRef(1);
  const pageSize = 20;

  const onPickerOpenChange = useCallback((key: string, isOpen: boolean) => {
    if (isOpen) setActivePickerKey(key);
    else if (activePickerKey === key) setActivePickerKey(null);
  }, [activePickerKey]);

  const getCaseReportFilter = useCallback(() => {
    const filter: any = { logic: 'and', filters: [] };
    const filters = [
      { field: 'caseId', operator: 'startswith', value: caseId.trim() },
      { field: 'callLocFormattedAddress', operator: 'contains', value: location.trim() },
      { field: 'npsdClientId', operator: 'eq', value: selectedClient },
      { field: 'incidentOccurredFrom', operator: 'gte', value: startDate ? convertInputDateToServerTime(startDate) : null },
      { field: 'incidentOccurredFrom', operator: 'lte', value: endDate ? convertInputDateToEndOfDayServerTime(endDate) : null },
    ];
    for (const elt of filters) {
      if (hasStringValue(elt.value)) filter.filters.push(elt);
    }
    return filter;
  }, [caseId, location, selectedClient, startDate, endDate]);

  const loadGrid = useCallback(async (page: number) => {
    setGridLoading(true);
    setSearching(true);
    try {
      const result = await api.caseReportSearch(page, pageSize, { data: { filter: getCaseReportFilter() } });
      setGridEntries(result.entries || []);
      const records = result.records || 0;
      const numPages = Math.max(1, Math.ceil(records / pageSize));
      setGridPager({ curPage: page, numPages, records, isVisible: records > 0, pageSize });
      setTimeout(() => {
        document.getElementById('caseReportSearchResultsCard')?.scrollIntoView();
      }, 0);
    } catch {
      // Error handled by API
    }
    setGridLoading(false);
    setSearching(false);
  }, [getCaseReportFilter]);

  const handleSearch = useCallback(() => {
    setShowResults(true);
    currentPageRef.current = 1;
    loadGrid(1);
  }, [loadGrid]);

  const handleGoToPage = useCallback((page: number) => {
    currentPageRef.current = page;
    loadGrid(page);
  }, [loadGrid]);

  const handleCellClick = useCallback(async ({ row, column }: any) => {
    if (column.field !== 'pdf') return;
    if (pdfDownloading[row.id]) return;
    setPdfDownloading(prev => ({ ...prev, [row.id]: true }));
    try {
      const result = await api.getCaseReportPdfUrl(row.id);
      if (result.uploaded && hasStringValue(result.signedURL)) {
        openPage(result.signedURL);
      } else {
        showToast('Error downloading Case Report PDF. It may still be generating - please try again in 15 seconds.', 'error');
      }
    } catch {
      showToast('Error downloading Case Report PDF.', 'error');
    }
    setPdfDownloading(prev => ({ ...prev, [row.id]: false }));
  }, [pdfDownloading]);

  const columns = useMemo(() => [
    {
      field: 'pdf', name: 'PDF', template(row: any) {
        return "<a class='caseReportPdfDownload' href='#'><i class='fas fa-download'></i></a>";
      },
    },
    { field: 'caseId', name: 'Case ID' },
    {
      field: 'natureOfCallValues', name: 'Nature of Call', template(row: any) {
        return displayNocValuesForGridTemplate(row);
      },
    },
    { field: 'callLocFormattedAddress', name: 'Event Location' },
  ], []);

  return (
    <>
      <div className="kstc-card kst-card-width">
        <div className="kstc-card__body">
          <h5 className="kstc-card__title">Case Reports</h5>
          <p className="kstc-card__text">Search Case Reports.</p>
          <div className="kstc-row kstc-mb-3 kstc-gy-3">
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Case ID</label>
              <input type="text" className="kstc-input" value={caseId} onChange={(e) => setCaseId(e.target.value)} />
            </div>
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Location</label>
              <input type="text" className="kstc-input" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Client</label>
              <Dropdownlist value={selectedClient} items={caseReportNpsdClientList} labelKey="name" valueKey="id" placeholder="Select Client" sortItems={false} onChange={(val) => setSelectedClient(val)} />
            </div>
          </div>
          <div className="kstc-row kstc-gy-3">
            <div className="kstc-col-sm-6">
              <label className="kstc-label">Start Date (Incident Occurred From)</label>
              <DateTimePicker value={startDate} type="date" appendToBody={true} onChange={setStartDate} onOpenChange={(val) => onPickerOpenChange('caseReportStart', val)} />
            </div>
            <div className="kstc-col-sm-6">
              <label className="kstc-label">End Date (Incident Occurred From)</label>
              <DateTimePicker value={endDate} type="date" appendToBody={true} onChange={setEndDate} onOpenChange={(val) => onPickerOpenChange('caseReportEnd', val)} />
            </div>
          </div>
          <div className="kstc-row kstc-mt-3 kstc-gy-3">
            <div className="kstc-col-sm-12">
              <button type="button" disabled={searching} onClick={handleSearch} className="kstc-btn kstc-btn--primary kstc-btn--lg kstc-float-end">
                {!searching ? <span>Search</span> : <><span className="kstc-spinner kstc-spinner--sm" role="status" aria-hidden="true"></span><span> Searching...</span></>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showResults && (
        <div id="caseReportSearchResultsCard" className="kstc-card kst-card-width">
          <div className="kstc-card__body">
            <h5 className="kstc-card__title">Case Reports - Search Results</h5>
            <KstDataGrid
              columns={columns}
              rows={{ entries: gridEntries }}
              pager={gridPager}
              loading={gridLoading}
              showcheckbox={false}
              showheader={false}
              onGoToPage={handleGoToPage}
              onCellClick={handleCellClick}
            />
          </div>
        </div>
      )}
    </>
  );
}
